import { AI_JOB_KINDS, JOB_CATEGORIES, AI_JOB_KIND_LABELS } from '@/constants/aiJobKinds';
import i18n from '@/plugins/i18n';

function labelForJobKindCode(code) {
  if (!code) {
    return '-';
  }
  const key = `aiJobKind.${code}`;
  if (i18n.te(key)) {
    return i18n.t(key);
  }
  return AI_JOB_KIND_LABELS[code] || code;
}

/**
 * 서버에서 받은 jobKinds를 정규화된 문자열 배열로 변환
 * @param {Array|string} jobKinds - 서버에서 받은 jobKinds (enum 객체 또는 문자열)
 * @returns {Array<string>} 정규화된 Job 종류 배열 (대문자)
 */
export const normalizeJobKinds = (jobKinds) => {
  if (!jobKinds) {
    return [];
  }

  if (!Array.isArray(jobKinds)) {
    jobKinds = [jobKinds];
  }

  return jobKinds.map(jobKind => {
    if (typeof jobKind === 'string') {
      return jobKind.toUpperCase();
    } else if (jobKind && jobKind.code) {
      // enum 객체인 경우 code 속성 사용 (예: "ir_loc" -> "IR_LOC")
      return jobKind.code.toUpperCase();
    } else if (jobKind && typeof jobKind === 'object') {
      // enum 객체인 경우 name 속성 확인
      return (jobKind.name || jobKind.toString()).toUpperCase();
    }
    return String(jobKind).toUpperCase();
  });
};

/**
 * Job 상태에서 실행 중인 RGB Job이 있는지 확인
 * @param {Object} job - AI Analysis Job 객체
 * @returns {boolean} RGB Job이 실행 중인지 여부
 */
export const hasRunningRgbJob = (job) => {
  if (!job || job.processStatus !== 'ING') {
    return false;
  }

  const jobKinds = normalizeJobKinds(job.jobKinds);
  return jobKinds.includes(AI_JOB_KINDS.RGB_LOC);
};

/**
 * Job 상태에서 실행 중인 Thermal Job이 있는지 확인
 * @param {Object} job - AI Analysis Job 객체
 * @returns {boolean} Thermal Job이 실행 중인지 여부
 */
export const hasRunningThermalJob = (job) => {
  if (!job || job.processStatus !== 'ING') {
    return false;
  }

  const jobKinds = normalizeJobKinds(job.jobKinds);
  return JOB_CATEGORIES.THERMAL.some(thermalJob => jobKinds.includes(thermalJob));
};

/**
 * Job 상태 정보 추출
 * @param {Object} job - AI Analysis Job 객체
 * @returns {Object} { isRgbRunning, isThermalRunning }
 */
export const extractJobRunningStatus = (jobs) => {
  const rgbJob = jobs.find(job => job.resourceType === "RGB");
  const thermalJob = jobs.find(job => job.resourceType === "IR");

  return {
    isRgbRunning: rgbJob && hasRunningRgbJob(rgbJob),
    isThermalRunning: thermalJob && hasRunningThermalJob(thermalJob)
  };
};

/**
 * Job 종류 배열을 현재 로케일 라벨 문자열로 변환
 * @param {Array|string} jobKinds - 서버에서 받은 jobKinds (enum 객체 또는 문자열)
 * @param {string} separator - 라벨 구분자 (기본값: ', ')
 * @returns {string} 로케일 라벨로 변환된 문자열
 */
export const formatJobKinds = (jobKinds, separator = ', ') => {
  if (!jobKinds || jobKinds.length === 0) {
    return '-';
  }

  const normalized = normalizeJobKinds(jobKinds);
  const labels = normalized.map(kind => AI_JOB_KIND_LABELS[kind] || kind);
  
  return labels.join(separator);
};

/**
 * 단일 Job 종류를 현재 로케일 라벨로 변환
 * @param {string|Object} jobKind - Job 종류 (enum 객체 또는 문자열)
 * @returns {string} 로케일 라벨
 */
export const getJobKindLabel = (jobKind) => {
  if (!jobKind) {
    return '-';
  }

  const normalized = normalizeJobKinds([jobKind]);
  if (normalized.length === 0) {
    return '-';
  }

  return labelForJobKindCode(normalized[0]);
};

